"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidImportModal/ConnectionsSection";

import { Icon } from "@ospweb/sdk-ui";
import React from "react";

import { ROUTER_PATH } from "@config/router";
import type { FinancialConnection } from "@features/api/memberApi";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { PortfolioConnectionCard } from "./PortfolioConnectionCard";
import { SectionEmptyState } from "./SectionEmptyState";
import { cn } from "utils";

const I18n = I18nUtil.createScoped(Translations);

export interface ConnectionsSectionProps {
  investConnections: FinancialConnection[];
  nonInvestConnections: FinancialConnection[];
  /** Disabled state propagated from panel linkLoading/binding. */
  disabled?: boolean;
  /** Manage button callback — receives connectionId for panel's link flow. */
  onManage: (connectionId: string) => void;
  /** Close My accounts before navigating to dashboard / portfolio. */
  onNavigateAway?: () => void;
}

interface SectionProps {
  title: string;
  subtitle: string;
  connections: FinancialConnection[];
  section: "invest" | "non_invest";
  /** Only shown when section has data. */
  viewLink?: { label: string; href: string };
  disabled?: boolean;
  onManage: (connectionId: string) => void;
  onNavigateAway?: () => void;
}

/**
 * A single partition (Invest or Banking).
 * Renders SectionEmptyState when no connections; otherwise renders cards + view link.
 */
const Section: React.FC<SectionProps> = ({
  title,
  subtitle,
  connections,
  section,
  viewLink,
  disabled = false,
  onManage,
  onNavigateAway,
}) => {
  const router = useShellRouter();
  const hasData = connections.length > 0;

  return (
    <div className="flex w-full flex-col gap-3">
      {/* Section header */}
      <div className="flex items-start justify-between overflow-hidden">
        <div className="flex min-w-px flex-1 flex-col gap-0.5">
          <p className="body_bold_l text-content_primary">{title}</p>
          <p
            className={cn("body_s text-content_tertiary", { hidden: !hasData })}
          >
            {subtitle}
          </p>
        </div>
        {hasData && viewLink ? (
          <div className="flex shrink-0 items-center py-1.5">
            <button
              type="button"
              onClick={() => {
                onNavigateAway?.();
                router.push(viewLink.href);
              }}
              className="inline-flex items-center gap-0.5 body_medium_s text-content_brand_primary"
              data-testid={`section-view-link-${section}`}
            >
              {viewLink.label}
              <Icon name="right-arrow-alt" size={16} className="shrink-0" />
            </button>
          </div>
        ) : null}
      </div>
      {/* Section body */}
      {hasData ? (
        <div className="flex w-full flex-col gap-3">
          {connections.map((connection) => (
            <PortfolioConnectionCard
              key={connection.connection_id ?? connection.institution_id}
              connection={connection}
              section={section}
              disabled={disabled}
              onManage={() => {
                const connectionId = connection.connection_id;
                if (connectionId) {
                  onManage(connectionId);
                }
              }}
            />
          ))}
        </div>
      ) : (
        <SectionEmptyState
          showSupportedLogos={section === "invest"}
          description={
            section === "invest"
              ? I18n.t(
                  "Link a brokerage, 401(k) or crypto wallet and I'll track your holdings.",
                )
              : I18n.t(
                  "Link a bank or card and I'll watch your spending and balances.",
                )
          }
        />
      )}
    </div>
  );
};

/**
 * Container for Invest + Banking partitions.
 * AC-2.2: Same connection split by account.type into different sections.
 *
 * 恒双分区展示（不做 accountTarget 过滤）：目标语境只保留在 Confirm 门控
 * （PortfolioAccountsModal 的 hasTargetCoverage）与 Plaid 连接弹窗的账户类型
 * 筛选，分区展示不过滤。
 */
export const ConnectionsSection: React.FC<ConnectionsSectionProps> = ({
  investConnections,
  nonInvestConnections,
  disabled = false,
  onManage,
  onNavigateAway,
}) => {
  return (
    <div className="flex flex-col gap-10">
      <Section
        title={I18n.t("Invest")}
        subtitle={I18n.t("Brokerage, retirement and crypto")}
        connections={investConnections}
        section="invest"
        viewLink={{
          label: I18n.t("View portfolio"),
          href: ROUTER_PATH.INVEST_PORTFOLIO,
        }}
        disabled={disabled}
        onManage={onManage}
        onNavigateAway={onNavigateAway}
      />
      <Section
        title={I18n.t("Banking")}
        subtitle={I18n.t("Checking, savings and credit")}
        connections={nonInvestConnections}
        section="non_invest"
        viewLink={{
          label: I18n.t("View dashboard"),
          href: ROUTER_PATH.PLAN_DASHBOARD,
        }}
        disabled={disabled}
        onManage={onManage}
        onNavigateAway={onNavigateAway}
      />
    </div>
  );
};
